import {useNavigate, useParams} from "react-router-dom";
import {Autocomplete, Box, Divider, Stack, Switch} from "@mui/material";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {SectorService} from "crqlar-react-common/dist/services/sector/sector.service";
import {ConfirmationSettings, Sector, SectorType, SerialReservationAutomationSettings} from "crqlar-react-common/dist/models/sector/sector.model";
import SettingsEntityHeader from "../layout/SettingsEntityHeader";
import {isRequiredTextMissing} from "src/utils/requiredField.util";
import DangerZone from "crqlar-react-common/dist/components/DangerZone";
import Property from "crqlar-react-common/dist/components/Property";
import ServiceInfoRow from "crqlar-react-common/dist/components/ServiceInfoRow";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useQueryClient} from "react-query";
import ConfirmationSettingsSection from "../newsletterAccount/ConfirmationSettingsSection";
import ExternalInfoPreview from "../common/ExternalInfoPreview";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import SectorIconPicker from "./SectorIconPicker";
import {DEFAULT_SECTOR_ICON_KEY_BY_TYPE} from "crqlar-react-common/dist/components/sectorIcons/sectorIcons";
import {useState} from "react";
import Form from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import {CANCELLATION_PHONE_TAG, editableSectorTags, mergeSectorTags} from "./sectorTags.util";

const SectorDetails = () => {

    useTrackScreenVisit('system sector details')

    //region State

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [sidebarIconChanged, setSidebarIconChanged] = useState(false)

    const sectorData = useEntity({
        get: async () => {
            if (id && id !== 'new') {
                return SectorService.getSectorById(id)
            } else {
                return {}
            }
        },
        create: SectorService.createSector,
        update: SectorService.updateSector,
        delete: (id) => SectorService.deleteSector({id: [id]}),
        onDeleted: () => navigate('/system/sectors', {replace: true}),
        onSaved: (sector, wasCreateAction, prev) => {
            // invalidate ['allSingleSector', sectorId]
            queryClient.invalidateQueries(['allSingleSector', sector.id])
            queryClient.invalidateQueries('allSectors')

            if (wasCreateAction) {
                if (sidebarIconChanged) {
                    window.location.replace(`/system/sectors/${sector.id}`)
                    return
                }
                navigate(`/system/sectors/${sector.id}`, {replace: true})
            } else if (sidebarIconChanged) {
                window.location.reload()
            }
        },
        isValid: (sector) => {
            return !!sector.name
        },
        cleanupBeforeSave: (sector) => {
            return {
                ...sector,
                name: sector.name?.trim()
            }
        }
    }, id)

    const sector = sectorData.item
    const setSector = sectorData.setItem
    const nameMissing = isRequiredTextMissing(sectorData, sector.name)
    const hasExternalInfo = !!sector.externalInfo?.length
    const showDangerZone = !!sector.id


    //endregion Sate

    //region Handlers

    const updateField = (field: keyof Sector, value: any) => {
        setSector({...sector, [field]: value})
    }

    const updateCancellationPhone = (value: string) => {
        const tags = {...(sector.tags || {})}
        if (value) {
            tags[CANCELLATION_PHONE_TAG] = value
        } else {
            delete tags[CANCELLATION_PHONE_TAG]
        }
        updateField('tags', tags)
    }

    const updateDataTags = (edited: Record<string, unknown>) => {
        updateField('tags', mergeSectorTags(sector.tags, edited))
    }

    //endregion Handlers

    //region UI

    const headerSection = (
        <SettingsEntityHeader
            title={sector.name}
            data={sectorData}
            back={() => navigate('/system/sectors')}
        />
    )

    const basicInfoSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Basic info')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Name')}
                    tooltip={t('Display label for this sector across settings, reservations, and booking screens.')}
                >
                    <TextField
                        id={'name'}
                        size={'small'}
                        value={sector.name ?? ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        fullWidth
                        required
                        error={nameMissing}
                    />
                </Property>

                <Property
                    title={t('Sector type')}
                    tooltip={t('Defines the sector behavior, such as restaurant, spa, accommodation, or weekly planner.')}
                >
                    <Autocomplete
                        id={'sectorType'}
                        options={[
                            undefined,
                            ...Object.keys(SectorType).map(key => key.toString())
                        ]}
                        getOptionLabel={(option) => option ? t(`Sector Type ${option}`) : t('(Not set)')}
                        value={sector.sectorType || ''}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                                size={'small'}
                            />
                        )}
                        fullWidth
                        onChange={(e, value) => {
                            updateField('sectorType', value)
                        }}
                    />
                </Property>

                <Property
                    title={t('Sidebar icon')}
                    tooltip={t('Icon used for this sector in dashboard navigation.')}
                >
                    <SectorIconPicker
                        value={sector.sidebarIconKey}
                        defaultIconKey={sector.sectorType
                            ? DEFAULT_SECTOR_ICON_KEY_BY_TYPE[sector.sectorType]
                            : undefined}
                        onChange={(value) => {
                            setSidebarIconChanged(true)
                            updateField('sidebarIconKey', value)
                        }}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const defaultsSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Sector defaults')}
            </Typography>

            <Stack gap={1}>
                <ServiceInfoRow
                    id={'service-find'}
                    tooltip={t('Default service used when creating reservations for this sector without another selected service.')}
                    serviceId={sector.defaultServiceId}
                    setServiceId={(serviceId) => {
                        updateField('defaultServiceId', serviceId)
                    }}
                    onDelete={() => {
                        updateField('defaultServiceId', undefined)
                    }}
                    sector={sector}
                />

                <Property
                    title={t('Timeline span')}
                    tooltip={t('Default calendar range shown when opening this sector.')}
                >
                    <Autocomplete
                        id={'timeline-span'}
                        options={['day', 'week', 'month', 'year']}
                        value={sector.defaultTimelineSpan || null}
                        getOptionLabel={v => t(`Timeline span ${v}`)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                            />
                        )}
                        onChange={(e, value) => {
                            updateField('defaultTimelineSpan', value)
                        }}
                        fullWidth
                        size={'small'}
                    />
                </Property>

                <Property
                    title={t('Timeline step')}
                    tooltip={t('Default time-slot granularity for this sector timeline.')}
                >
                    <Autocomplete
                        id={'timeline-step'}
                        options={[15, 30, 60, 120, 240, 360, 720, 1440]}
                        value={sector.defaultTimelineStep || null}
                        getOptionLabel={v => {
                            if (v > 60) {
                                let hours = `${Math.floor(v / 60)}`
                                return `${hours} h`
                            }
                            return `${v} min`
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                fullWidth
                            />
                        )}
                        onChange={(e, value) => {
                            updateField('defaultTimelineStep', value)
                        }}
                        fullWidth
                        size={'small'}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const updateConfirmationSetting = (event: keyof ConfirmationSettings, value: any) => {
        updateField('notificationSettings', {
            ...sector?.notificationSettings,
            confirmationSettings: {
                ...sector?.notificationSettings?.confirmationSettings,
                [event]: value
            }
        })
    }

    const updateAutomationSetting = (field: keyof SerialReservationAutomationSettings, value: boolean) => {
        updateField('serialReservationAutomation', {
            ...sector?.serialReservationAutomation,
            [field]: value
        })
    }

    const automationSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Automation')}
            </Typography>

            <Stack gap={1}>
                <Property
                    title={t('Auto-cancel series on early departure')}
                    tooltip={t('When a guest departs early, automatically cancel their serial restaurant reservations that fall after the new departure date.')}
                >
                    <Switch
                        id={'auto-cancel-early-departure'}
                        checked={!!sector.serialReservationAutomation?.autoCancelOnEarlyDeparture}
                        onChange={(e) => updateAutomationSetting('autoCancelOnEarlyDeparture', e.target.checked)}
                    />
                </Property>

                <Property
                    title={t('Auto-create series on prolonged stay')}
                    tooltip={t('When a guest extends their stay, automatically create the restaurant bookings for the newly-covered days, matching their existing meal-series pattern.')}
                >
                    <Switch
                        id={'auto-create-prolonged-stay'}
                        checked={!!sector.serialReservationAutomation?.autoCreateOnProlongedStay}
                        onChange={(e) => updateAutomationSetting('autoCreateOnProlongedStay', e.target.checked)}
                    />
                </Property>
            </Stack>
        </Stack>
    )

    const sendGridEmailSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('SendGrid Email Templates')}
            </Typography>

            <ConfirmationSettingsSection
                confirmationSettings={sector?.notificationSettings?.confirmationSettings}
                showSectionTitle={false}
                section={'sendgrid'}
                onSettingChange={updateConfirmationSetting}
            />
        </Stack>
    )

    const advancedSettingsSection = (
        <AdditionalProperties title={t('Advanced Settings')} variant={'outlined'}>
            <Property
                title={t('Cancellation phone')}
            >
                <TextField
                    id={'cancellation-phone'}
                    size={'small'}
                    value={(sector.tags?.[CANCELLATION_PHONE_TAG] as string) || ''}
                    onChange={(e) => updateCancellationPhone(e.target.value)}
                    placeholder={'+4365838561-40'}
                    fullWidth
                />
            </Property>

            <Property
                title={t('Data tags')}
                tooltip={t('Custom metadata used by app integrations and guest-app behavior.')}
            >
                <Form
                    formData={editableSectorTags(sector.tags)}
                    onChange={(e) => updateDataTags(e.formData)}
                    validator={validator}
                    formContext={{submit: false}}
                    templates={{ButtonTemplates: {SubmitButton: () => <div/>}}}
                    schema={{
                        type: 'object',
                        additionalProperties: [{type: 'string'}],
                        properties: {}
                    } as any}
                    uiSchema={{}}
                />
            </Property>
        </AdditionalProperties>
    )

    const otherSection = (
        <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Other')}
            </Typography>

            <ConfirmationSettingsSection
                confirmationSettings={sector?.notificationSettings?.confirmationSettings}
                showSectionTitle={false}
                section={'other'}
                onSettingChange={updateConfirmationSetting}
            />

            {advancedSettingsSection}
        </Stack>
    )

    const dangerZoneSection = (
        <Stack gap={{xs: 2.5, sm: 2}}>
            <DangerZone
                title={t('Delete sector')}
                description={t('This permanently deletes the sector. Services and reservations assigned to it may stop working correctly. This action cannot be undone.')}
                buttonLabel={t('Delete')}
                loading={sectorData.saving}
                onDelete={() => sectorData.removeNow()}
            />

            <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                {t('Last updated')}: {sector.updateDate?.toLocaleString() || '-'}
            </Typography>
        </Stack>
    )

    //endregion UI

    const sectorDataUI = (
        <Stack
            sx={{
                width: '100%',
                maxWidth: '900px',
                mx: 'auto',
                mt: 3,
                gap: 6,
            }}
        >
            {basicInfoSection}
            <Divider/>
            {defaultsSection}
            {sector.sectorType === SectorType.RESTAURANT && (
                <>
                    <Divider/>
                    {automationSection}
                </>
            )}
            <Divider/>
            {sendGridEmailSection}
            <Divider/>
            {otherSection}

            {(hasExternalInfo || showDangerZone) && <Divider/>}

            {hasExternalInfo && (
                <>
                    <ExternalInfoPreview externalInfo={sector.externalInfo}
                                         setExternalInfo={e => updateField('externalInfo', e)}/>
                    {showDangerZone && <Divider/>}
                </>
            )}

            {showDangerZone && (
                <>
                    {dangerZoneSection}
                </>
            )}
        </Stack>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            {headerSection}
            {sectorDataUI}
        </Box>
    );
}

export default SectorDetails;
